"use client"
import { useState, useMemo } from "react";
import { InputBase, Stack, Typography, Collapse, List, ListItem, ListItemText, ListItemButton, ListItemIcon, Checkbox, IconButton, ListSubheader, Divider, TextareaAutosize, ClickAwayListener } from "@mui/material";
import Footer from "@/components/layout/footer";
import SideTabbedButton from "@/components/generic/SideTabbedButton";
import theme from "@/theme/theme";
import Image from "next/image";

import contactPageBg from "@/assets/images/contactPage/contactPageBg.svg?url";

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ClearIcon from '@mui/icons-material/Clear';
import arrow from "@/assets/images/icons/arrow.webp"

interface InquiryType {
    title: string;
    options: string[];
}

type SelectedInquiryMap = Record<string, string[]>;

const entityTypes = ["Individual", "Startup", "Corporation", "Institution"];
const inquiryTypes: InquiryType[] = [
    {
        title: "Smart Contracts",
        options: [
            "ERC Standards",
            "Custom Logic",
            "DApps",
            "Oracles",
            "Audits",
        ]
    },
    {
        title: "Infra & Protocols",
        options: [
            "Layer Setup",
            "Chain Infra",
            "Tooling",
            "DevOps Pipelines",
        ]
    },
    {
        title: "AI Systems",
        options: [
            "LLM Agents",
            "Prediction Models",
            "GenAI Content",
            "AI SDKs",
        ]
    },
    {
        title: "Product Dev",
        options: [
            "UX Design",
            "App Dev",
            "Backend",
            "APIs",
            "Database",
        ]
    },
    {
        title: "ZK & PQC",
        options: [
            "Zero Knowledge Proof",
            "Cryptography Models",
            "Post-Quantum Migration",
        ]
    },
    {
        title: "GTM & Legal",
        options: [
            "MVP Validation",
            "Token Legal",
            "Compliance",
            "EMEA Strategy",
        ]
    },
]

type Status = "idle" | "loading" | "success" | "error";

export default function ContactPage() {

    const [fullName, setFullName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [message, setMessage] = useState("");
    const [agreeToProcessing, setAgreeToProcessing] = useState(true);

    const [status, setStatus] = useState<Status>("idle");
    const [errorMsg, setErrorMsg] = useState("");

    const [inquirySearch, setInquirySearch] = useState("");

    const filteredInquiryTypes = useMemo(() => {
        return inquiryTypes
            .map((group) => ({
                ...group,
                options: group.options.filter(
                    (opt) =>
                        opt.toLowerCase().includes(inquirySearch.toLowerCase()) ||
                        group.title.toLowerCase().includes(inquirySearch.toLowerCase())
                ),
            }))
            .filter((group) => group.options.length > 0);
    }, [inquirySearch]);

    const [openEntityTypes, setOpenEntityTypes] = useState<boolean>(false);
    const [selectedEntityTypes, setSelectedEntityTypes] = useState<string[]>([]);

    const handleToggleEntityType = (type: string) => {
        setSelectedEntityTypes(prev => prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]);
    };

    const [openInquiryTypes, setOpenInquiryTypes] = useState<boolean>(false);
    const [selectedInquiryOptions, setSelectedInquiryOptions] = useState<SelectedInquiryMap>({});

    const handleToggleInquiryOption = (groupTitle: string, option: string) => {
        setSelectedInquiryOptions(prev => {
            const current = new Set(prev[groupTitle] ?? []);
            if (current.has(option)) {
                current.delete(option);
            } else {
                current.add(option);
            }
            return { ...prev, [groupTitle]: Array.from(current) };
        });
    };

    const handleToggleEntityMenu = () => {
        setOpenEntityTypes((prev) => !prev);
        setOpenInquiryTypes(false);
    };

    const handleToggleInquiryMenu = () => {
        setOpenInquiryTypes((prev) => {
            if (!prev) setInquirySearch("");
            return !prev;
        });
        setOpenEntityTypes(false);
    };

    const handleSubmit = async () => {
        if (status === "loading") return;

        if (!fullName.trim() || !email.trim()) {
            setStatus("error");
            setErrorMsg("Full name and email are required.");
            return;
        }

        if (!agreeToProcessing) {
            setStatus("error");
            setErrorMsg("You must agree to the processing of personal data.");
            return;
        }

        setStatus("loading");
        setErrorMsg("");

        const filteredInquiry: SelectedInquiryMap = {};
        for (const [group, opts] of Object.entries(selectedInquiryOptions)) {
            if (opts.length > 0) filteredInquiry[group] = opts;
        }

        try {
            const res = await fetch("/api/contact", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    fullName: fullName.trim(),
                    email: email.trim(),
                    phone: phone.trim() || null,
                    entityTypes: selectedEntityTypes.length > 0 ? selectedEntityTypes : null,
                    inquiryOptions: Object.keys(filteredInquiry).length > 0 ? filteredInquiry : null,
                    message: message.trim() || null,
                    agreeToProcessing: true,
                }),
            });

            const data = (await res.json()) as { ok: boolean; error?: string };

            if (!res.ok || !data.ok) {
                throw new Error(data.error ?? "Something went wrong.");
            }

            setStatus("success");
            setFullName("");
            setEmail("");
            setPhone("");
            setMessage("");
            setSelectedEntityTypes([]);
            setSelectedInquiryOptions({});
        } catch (e) {
            setStatus("error");
            setErrorMsg(e instanceof Error ? e.message : "Something went wrong.");
        }
    };

    return (
        <Stack width={"100%"} alignItems={"center"} gap={{ xs: 4, md: 8 }}>
            <Stack width={"100%"} height="55vh" sx={{
                borderBottomLeftRadius: 20,
                borderBottomRightRadius: 20,
                overflow: "hidden",
                position: "relative",
                zIndex: 1,
                display: { xs: "none", md: "block" },
            }}>
                <Image src={contactPageBg} alt="contact page background" width={1000} height={1000} style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                }} />
            </Stack>
            <Stack width={"100%"} height="35vh" sx={{
                borderBottomLeftRadius: 20,
                borderBottomRightRadius: 20,
                overflow: "hidden",
                position: "relative",
                zIndex: 1,
                display: { xs: "block", md: "none" },
            }}>
                <Image src={contactPageBg} alt="contact page background" width={1000} height={1000} style={{
                    width: "100%",
                    height: "70%",
                    objectFit: "cover",
                    marginTop: "-10%",
                    transform: "scaleX(-1)",
                }} />
            </Stack>
            <Stack width={"100%"} height="10vh" sx={{
                display: { xs: "block", md: "none" },
                background: "linear-gradient(180deg, rgba(11, 11, 11, 0) 0%, #0B0B0B 100%)",
                marginTop: { xs: "-58%", sm: "-30vh" },
                position: "relative",
                zIndex: 2,
            }}>

            </Stack>
            <Stack
                width={{ xs: "90%", lg: "80%" }}
                marginX="auto"
                direction={{ xs: "column", md: "row" }}
                alignItems="stretch"
                justifyContent="space-between"
                marginTop={{ xs: "-42%", sm: "-15vh", md: "-25%" }}
                position={"relative"}
                sx={{
                    zIndex: 2,
                }}
            >
                <Stack width={{ xs: "100%", md: "50%" }} flexGrow={1} justifyContent="space-between" marginBottom={{ xs: 4, md: 16 }} paddingRight={{ xs: 0, md: 3, lg: 0 }}>
                    <Stack width="100%" gap={2}>
                        <Typography
                            variant="h1"
                            component="h1"
                            fontSize={{ xs: "2rem !important", md: "2.75rem !important", lg: "3.75rem !important" }}
                            className="chno"
                            fontWeight={500}
                            textTransform="uppercase"
                        >
                            Let’s Build the Future Together
                        </Typography>
                    </Stack>
                </Stack>
                <Stack width={{ xs: "100%", md: "50%" }} flexGrow={1} gap={2} sx={{
                    background: { xs: "transparent", md: "#0F0F0FB2" },
                    border: { xs: "none", md: "1px solid #181818" },
                    backdropFilter: { xs: "none", md: "blur(89.80000305175781px)" },
                    borderRadius: { xs: 0, md: 2 },
                    paddingY: { xs: 2, md: 4 },
                    paddingX: { xs: 2, md: 4 },
                    alignItems: "center",
                    gap: 4,
                }}>
                    <Typography
                        variant="body1"
                        component="p"
                        fontWeight={300}
                        textAlign="center"
                        sx={{
                            width: "90%",
                        }}
                    >
                        Complete the form and our team will respond with the information and support you need.
                    </Typography>
                    <Stack width="100%" gap={2}>
                        <InputBase
                            placeholder="Full Name"
                            inputProps={{ 'aria-label': 'Full Name' }}
                            type="text"
                            value={fullName}
                            onChange={(e) => setFullName(e.target.value)}
                            disabled={status === "loading"}
                            sx={{
                                width: "100%",
                                border: "1px solid #2F2F2F",
                                borderRadius: 2,
                                padding: 1,
                                background: "transparent",
                                color: theme.palette.text.primary,
                                "&::placeholder": {
                                    color: theme.palette.text.secondary,
                                },
                                "&:hover": {
                                    borderColor: "#FFFFFF",
                                }
                            }}
                        />
                        <InputBase
                            placeholder="Email"
                            inputProps={{ 'aria-label': 'Email Address' }}
                            type="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            disabled={status === "loading"}
                            sx={{
                                width: "100%",
                                border: "1px solid #2F2F2F",
                                borderRadius: 2,
                                padding: 1,
                                background: "transparent",
                                color: theme.palette.text.primary,
                                "&::placeholder": {
                                    color: theme.palette.text.secondary,
                                },
                                "&:hover": {
                                    borderColor: "#FFFFFF",
                                }
                            }}
                        />
                        <InputBase
                            placeholder="Phone Number"
                            inputProps={{ 'aria-label': 'Phone Number' }}
                            type="tel"
                            value={phone}
                            onChange={(e) => setPhone(e.target.value)}
                            disabled={status === "loading"}
                            sx={{
                                width: "100%",
                                border: "1px solid #2F2F2F",
                                borderRadius: 2,
                                padding: 1,
                                background: "transparent",
                                color: theme.palette.text.primary,
                                "&::placeholder": {
                                    color: theme.palette.text.secondary,
                                },
                                "&:hover": {
                                    borderColor: "#FFFFFF",
                                }
                            }}
                        />
                        <ClickAwayListener onClickAway={() => { setOpenEntityTypes(false); }}>
                            <Stack 
                                width="100%" 
                                alignItems="center" 
                                paddingX={1} 
                                paddingY={1.25} 
                                borderRadius={2} 
                                sx={{
                                    border: "1px solid #2F2F2F",
                                    position: "relative",
                                    zIndex: openEntityTypes ? 10 : 1,
                                    "&:hover": {
                                        borderColor: "#FFFFFF",
                                    }
                                }}
                            >
                                <Stack
                                    component="div"
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={openEntityTypes}
                                    aria-haspopup="listbox"
                                    aria-controls="entity-types-list"
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggleEntityMenu(); } }}
                                    width="100%"
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    onClick={handleToggleEntityMenu}
                                    sx={{
                                        cursor: "pointer",
                                        paddingLeft: 0.5,
                                    }}
                                >
                                    {selectedEntityTypes.length > 0 ? (
                                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                            {selectedEntityTypes.map((type, index) => (
                                                <Stack key={`type-${index}`} direction="row" alignItems="center" gap={1} sx={{
                                                    border: "0.3px solid #656565",
                                                    backgroundColor: "#0F0F0F",
                                                    borderRadius: 1,
                                                    paddingX: 1,
                                                    paddingY: 0.5,
                                                }}>
                                                    <Typography variant="subtitle2" fontWeight={400}>{type}</Typography>
                                                    <IconButton onClick={(e) => { e.stopPropagation(); handleToggleEntityType(type); }} sx={{ backgroundColor: "#7F7E7E", padding: 0.25, display: "flex", alignItems: "center", justifyContent: "center", "&:hover": { backgroundColor: "#7F7E7E", opacity: 0.8 } }}>
                                                        <ClearIcon fontSize="small" sx={{ fontSize: "0.8rem" }} />
                                                    </IconButton>
                                                </Stack>
                                            ))}
                                        </Stack>
                                    ) : (
                                        <Typography variant="subtitle1" fontWeight={400} color={theme.palette.text.secondary}>
                                            Type of project
                                        </Typography>
                                    )}
                                    <KeyboardArrowDownIcon fontSize="small" sx={{
                                        color: theme.palette.text.secondary,
                                        transition: "transform 200ms ease",
                                        transform: openEntityTypes ? "rotate(180deg)" : "rotate(0deg)",
                                    }} />
                                </Stack>

                                <Collapse 
                                    in={openEntityTypes} 
                                    sx={{
                                        width: "calc(100% + 2px)",
                                        position: "absolute",
                                        top: "100%",
                                        left: -1,
                                        zIndex: 11,
                                        bgcolor: "#0B0B0B",
                                        border: "1px solid #2F2F2F",
                                        borderTop: "none",
                                        borderRadius: "0 0 8px 8px",
                                        boxShadow: "0px 10px 20px rgba(0,0,0,0.5)",
                                        marginTop: "1px",
                                    }}
                                >
                                    <Stack width="100%" id="entity-types-list" role="listbox" sx={{ maxHeight: "250px", overflowY: "auto", p: 1 }}>
                                        <List dense disablePadding sx={{ width: "100%", gap: 1 }}>
                                            {entityTypes.map((type, index) => (
                                                <ListItem key={`type-${index}`} role="option" aria-selected={selectedEntityTypes.includes(type)} sx={{ width: "100%", padding: 0 }}>
                                                    <ListItemButton onClick={() => { handleToggleEntityType(type); setOpenEntityTypes(false); }} dense sx={{ borderRadius: 1 }}>
                                                        <ListItemIcon sx={{ minWidth: 0 }}>
                                                            <Checkbox
                                                                edge="start"
                                                                checked={selectedEntityTypes.includes(type)}
                                                                disableRipple
                                                                sx={{
                                                                    color: theme.palette.text.secondary,
                                                                    "&.Mui-checked": { color: theme.palette.text.primary },
                                                                }}
                                                            />
                                                        </ListItemIcon>
                                                        <ListItemText primary={type} sx={{ color: theme.palette.text.primary }} />
                                                    </ListItemButton>
                                                </ListItem>
                                            ))}
                                        </List>
                                    </Stack>
                                </Collapse>
                            </Stack>
                            </ClickAwayListener>
                            <ClickAwayListener onClickAway={() => { setOpenInquiryTypes(false); }}>
                            <Stack
                                width="100%"
                                alignItems="center"
                                paddingX={1}
                                paddingY={1.25}
                                borderRadius={2}
                                sx={{
                                    border: "1px solid #2F2F2F",
                                    position: "relative",
                                    zIndex: openInquiryTypes ? 10 : 1,
                                    "&:hover": { borderColor: "#FFFFFF" },
                                }}
                            >
                                <Stack
                                    component="div"
                                    role="button"
                                    tabIndex={0}
                                    aria-expanded={openInquiryTypes}
                                    aria-haspopup="listbox"
                                    aria-controls="inquiry-types-list"
                                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleToggleInquiryMenu();} }}
                                    width="100%"
                                    direction="row"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    onClick={handleToggleInquiryMenu}
                                    sx={{ cursor: "pointer", paddingLeft: 0.5, minHeight: "40px" }}
                                >
                                    {Object.entries(selectedInquiryOptions).some(([, arr]) => arr.length) ? (
                                        <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                            {Object.entries(selectedInquiryOptions).flatMap(([group, opts]) =>
                                                opts.map((opt, i) => (
                                                    <Stack
                                                        key={`${group}-${opt}-${i}`}
                                                        direction="row"
                                                        alignItems="center"
                                                        gap={1}
                                                        sx={{
                                                            border: "0.3px solid #656565",
                                                            backgroundColor: "#0F0F0F",
                                                            borderRadius: 1,
                                                            px: 1,
                                                            py: 0.5,
                                                        }}
                                                    >
                                                        <Typography variant="subtitle2" fontWeight={400}>
                                                            {opt}
                                                        </Typography>
                                                        <IconButton
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                handleToggleInquiryOption(group, opt);
                                                            }}
                                                            sx={{
                                                                backgroundColor: "#7F7E7E",
                                                                p: 0.25,
                                                                display: "flex",
                                                                alignItems: "center",
                                                                justifyContent: "center",
                                                                "&:hover": { backgroundColor: "#7F7E7E", opacity: 0.8 },
                                                            }}
                                                        >
                                                            <ClearIcon fontSize="small" sx={{ fontSize: "0.8rem" }} />
                                                        </IconButton>
                                                    </Stack>
                                                ))
                                            )}
                                        </Stack>
                                    ) : (
                                        <Typography
                                            variant="subtitle1"
                                            fontWeight={400}
                                            color={theme.palette.text.secondary}
                                        >
                                            Service detail
                                        </Typography>
                                    )}

                                    <KeyboardArrowDownIcon
                                        fontSize="small"
                                        sx={{
                                            color: theme.palette.text.secondary,
                                            transition: "transform 200ms ease",
                                            transform: openInquiryTypes ? "rotate(180deg)" : "rotate(0deg)",
                                        }}
                                    />
                                </Stack>

                                <Collapse
                                    in={openInquiryTypes}
                                    sx={{
                                        position: "absolute",
                                        top: "100%",
                                        left: -1,
                                        width: "calc(100% + 2px)",
                                        zIndex: 11,
                                        bgcolor: "#0B0B0B",
                                        border: "1px solid #2F2F2F",
                                        borderTop: "none",
                                        borderRadius: "0 0 8px 8px",
                                        boxShadow: "0px 10px 20px rgba(0,0,0,0.5)",
                                        maxHeight: 300,
                                        overflowY: "auto",
                                        marginTop: "1px",
                                    }}
                                >
                                    <Stack width="100%" sx={{ p: 1 }} gap={1}>
                                        <InputBase
                                            placeholder="Search services..."
                                            inputProps={{ 'aria-label': 'Search service options' }}
                                            value={inquirySearch}
                                            onChange={(e) => setInquirySearch(e.target.value)}
                                            onClick={(e) => e.stopPropagation()}
                                            sx={{
                                                width: "100%",
                                                bgcolor: "#1A1A1A",
                                                borderRadius: 1.5,
                                                px: 1.5,
                                                py: 0.5,
                                                fontSize: "0.875rem",
                                                border: "1px solid #333",
                                                color: "white",
                                                "&:focus-within": { borderColor: theme.palette.primary.main }
                                            }}
                                        />
                                        <List id="inquiry-types-list" role="listbox" dense disablePadding sx={{ width: "100%" }}>
                                            {filteredInquiryTypes.length > 0 ? (
                                                filteredInquiryTypes.map((group, gi) => (
                                                    <Stack key={`group-${gi}`} sx={{ mb: 1 }}>
                                                        <ListSubheader
                                                            disableSticky
                                                            sx={{
                                                                pl: 1, pt: 1,
                                                                bgcolor: "transparent",
                                                                fontSize: "0.9rem",
                                                                color: theme.palette.text.secondary,
                                                                textTransform: "uppercase",
                                                                letterSpacing: 1
                                                            }}
                                                        >
                                                            {group.title}
                                                        </ListSubheader>

                                                        {group.options.map((opt, oi) => {
                                                            const checked = (selectedInquiryOptions[group.title] ?? []).includes(opt);
                                                            return (
                                                                <ListItem key={`opt-${gi}-${oi}`} sx={{ px: 0, py: 0 }}>
                                                                    <ListItemButton
                                                                        onClick={() => handleToggleInquiryOption(group.title, opt)}
                                                                        dense
                                                                        sx={{ pl: 1, borderRadius: 1 }}
                                                                    >
                                                                        <ListItemIcon sx={{ minWidth: 0 }}>
                                                                            <Checkbox
                                                                                edge="start"
                                                                                checked={checked}
                                                                                disableRipple
                                                                                sx={{
                                                                                    pr: 0,
                                                                                    color: checked ? theme.palette.text.primary : theme.palette.text.secondary,
                                                                                    "&.Mui-checked": { color: theme.palette.text.primary },
                                                                                }}
                                                                            />
                                                                        </ListItemIcon>
                                                                        <ListItemText
                                                                            primary={opt}
                                                                            sx={{
                                                                                fontWeight: 300,
                                                                                color: checked ? theme.palette.text.primary : theme.palette.text.secondary,
                                                                            }}
                                                                        />
                                                                    </ListItemButton>
                                                                </ListItem>
                                                            );
                                                        })}
                                                        {gi < filteredInquiryTypes.length - 1 && (
                                                            <Divider sx={{ mt: 1, borderColor: "#242424" }} />
                                                        )}
                                                    </Stack>
                                                ))
                                            ) : (
                                                <Typography sx={{ p: 2, textAlign: "center", color: theme.palette.text.secondary }}>
                                                    No services found.
                                                </Typography>
                                            )}
                                        </List>
                                    </Stack>
                                </Collapse>
                            </Stack>
                        </ClickAwayListener>
                        <TextareaAutosize
                            placeholder="Message"
                            aria-label="Your message"
                            minRows={4}
                            value={message}
                            onChange={(e) => setMessage(e.target.value)}
                            disabled={status === "loading"}
                            style={{
                                width: "100%",
                                border: "1px solid #2F2F2F",
                                borderRadius: "0.6rem",
                                padding: "0.6rem",
                                background: "transparent",
                                color: theme.palette.text.primary,
                            }}
                        />
                        <Stack width="100%" direction={{ xs: "column", lg: "row" }} alignItems="center" justifyContent={{ xs: "center", lg: "space-between" }} mt={2} gap={{ xs: 2, lg: 0 }}>
                            <Stack width={{ xs: "100%", lg: "fit-content" }} direction="row" alignItems="center" justifyContent={{ xs: "start", lg: "center" }} gap={1}>
                                <Checkbox
                                    edge="start"
                                    slotProps={{ input: { 'aria-label': 'Agree to personal data processing' } }}
                                    checked={agreeToProcessing}
                                    onChange={(e) => setAgreeToProcessing(e.target.checked)}
                                    disabled={status === "loading"}
                                    disableRipple
                                    sx={{
                                        paddingRight: 0,
                                        color: theme.palette.text.primary,
                                        "&.Mui-checked": { color: theme.palette.text.primary },
                                    }}
                                />
                                <Typography component="span" variant="body2" fontWeight={300} color={theme.palette.text.secondary}>
                                    I agree to the processing of personal data
                                </Typography>
                            </Stack>
                            <Stack display={{ xs: "none", lg: "block" }} width="fit-content">
                                <SideTabbedButton paddingX={18} hoverShiftX={1.5} hoverShiftY={-1} action={handleSubmit} disabled={status === "loading"}>
                                    <div className="flex items-center justify-center gap-1">
                                        <Typography component="span" variant="h6" marginRight={0.5}>
                                            {status === "loading" ? "Sending..." : "Send"}
                                        </Typography>
                                        <span className="arrow-clip">
                                            <span className="arrow-slide">
                                                <Image src={arrow} alt="arrow animation" width={20} height={20} style={{ display: "block" }} />
                                            </span>
                                        </span>
                                    </div>
                                </SideTabbedButton>
                            </Stack>
                            <Stack display={{ xs: "block", lg: "none" }} width="100%" justifyContent="center" alignItems="center">
                                <SideTabbedButton fullWidth paddingX={18} hoverShiftX={1.5} hoverShiftY={-1} action={handleSubmit} disabled={status === "loading"}>
                                    <div className="flex items-center justify-center gap-1">
                                        <Typography component="span" variant="h6" marginRight={0.5}>
                                            {status === "loading" ? "Sending..." : "Send"}
                                        </Typography>
                                        <span className="arrow-clip">
                                            <span className="arrow-slide">
                                                <Image src={arrow} alt="arrow animation" width={20} height={20} style={{ display: "block" }} />
                                            </span>
                                        </span>
                                    </div>
                                </SideTabbedButton>
                            </Stack>
                        </Stack>
                        {status === "error" && (
                            <Typography variant="body2" role="alert" color="error" textAlign="center">
                                {errorMsg}
                            </Typography>
                        )}
                        {status === "success" && (
                            <Typography variant="body2" role="alert" color="success.main" textAlign="center">
                                Thank you! Your message has been sent successfully.
                            </Typography>
                        )}
                        <Stack display={{ xs: "block", md: "none" }} width="100%" justifyContent="center" alignItems="center" sx={{
                            backgroundColor: "#0F0F0F",
                            border: "1px solid #2F2F2F",
                            borderRadius: 2,
                            paddingY: 2,
                            paddingX: 4,
                            marginTop: 2,
                        }}>
                            <Typography
                                variant="h6"
                                component="p"
                                fontWeight={500}
                                color={theme.palette.text.primary}
                                textAlign="center"
                                marginBottom={2.5}
                            >
                                Stay in contact with us
                            </Typography>
                        </Stack>
                    </Stack>
                </Stack>
            </Stack>

            <Stack width={"100%"} alignItems={"center"} position={"relative"} sx={{
                borderTop: `1px solid ${theme.palette.brand.border1.main}`,
                marginTop: 6,
            }}>
                <Footer />
            </Stack>
        </Stack>
    );
}
